// Service Package Validation Schemas

import {
  ServicePackageFormData,
  ValidationErrors,
  StepValidationResult,
} from '../types/servicePackage'

export const validateStep1Basics = (
  data: ServicePackageFormData
): StepValidationResult => {
  const errors: ValidationErrors = {}

  // Service Title validation
  if (!data.basics.serviceTitle) {
    errors['serviceTitle'] = 'Service title is required'
  } else if (data.basics.serviceTitle.length < 5) {
    errors['serviceTitle'] = 'Service title must be at least 5 characters'
  } else if (data.basics.serviceTitle.length > 80) {
    errors['serviceTitle'] = 'Service title must be 80 characters or less'
  }

  // Discipline validation
  if (!data.basics.discipline) {
    errors['discipline'] = 'Discipline is required'
  }

  // Modality validation
  if (!data.basics.modality || data.basics.modality.length === 0) {
    errors['modality'] = 'At least one modality is required'
  }

  // Short Description validation
  if (!data.basics.shortDescription) {
    errors['shortDescription'] = 'Short description is required'
  } else if (data.basics.shortDescription.length < 40) {
    errors['shortDescription'] =
      'Short description must be at least 40 characters'
  } else if (data.basics.shortDescription.length > 300) {
    errors['shortDescription'] =
      'Short description must be 300 characters or less'
  }

  // Detailed Description validation (optional)
  if (
    data.basics.detailedDescription &&
    data.basics.detailedDescription.length > 5000
  ) {
    errors['detailedDescription'] =
      'Detailed description must be 5000 characters or less'
  }

  // Age Range validation (optional)
  if (data.basics.ageRange.min !== null) {
    if (data.basics.ageRange.min < 1) {
      errors['ageRange.min'] = 'Minimum age must be 1 or older'
    }
  }
  if (data.basics.ageRange.max !== null) {
    if (data.basics.ageRange.max < 1) {
      errors['ageRange.max'] = 'Maximum age must be 1 or older'
    }
  }
  if (data.basics.ageRange.min !== null && data.basics.ageRange.max !== null) {
    if (data.basics.ageRange.min > data.basics.ageRange.max) {
      errors['ageRange'] = 'Minimum age cannot be greater than maximum age'
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const validateStep2ClinicalCoding = (
  data: ServicePackageFormData
): StepValidationResult => {
  const errors: ValidationErrors = {}

  // CPT Codes validation
  if (
    !data.clinicalCoding.cptCodes ||
    data.clinicalCoding.cptCodes.length === 0
  ) {
    errors.cptCodes = 'At least one CPT code is required'
  } else {
    data.clinicalCoding.cptCodes.forEach((cptCode, index) => {
      if (!cptCode.code.trim()) {
        errors[`cptCodes.${index}.code`] = 'CPT code is required'
      }
      if (!cptCode.description.trim()) {
        errors[`cptCodes.${index}.description`] = 'Description is required'
      }
      if (cptCode.quantity <= 0) {
        errors[`cptCodes.${index}.quantity`] = 'Quantity must be greater than 0'
      }
    })
  }

  // ICD-10 Codes validation (optional but if provided, must be valid)
  if (
    data.clinicalCoding.icd10Codes &&
    data.clinicalCoding.icd10Codes.length > 10
  ) {
    errors.icd10Codes = 'Maximum 10 ICD-10 codes allowed'
  }

  if (data.clinicalCoding.icd10Codes) {
    data.clinicalCoding.icd10Codes.forEach((icd10Code, index) => {
      if (!icd10Code.code.trim()) {
        errors[`icd10Codes.${index}.code`] = 'ICD-10 code is required'
      }
    })
  }

  // Medical necessity note validation
  if (
    data.clinicalCoding.medicalNecessityNote &&
    data.clinicalCoding.medicalNecessityNote.length > 1000
  ) {
    errors.medicalNecessityNote =
      'Medical necessity note must be 1000 characters or less'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const validateStep3PricingBilling = (
  data: ServicePackageFormData
): StepValidationResult => {
  const errors: ValidationErrors = {}

  // Private pay rate validation - always required
  if (
    !data.pricingBilling.privatePayRate ||
    data.pricingBilling.privatePayRate <= 0
  ) {
    errors.privatePayRate =
      'Private pay rate is required and must be greater than 0'
  }

  // Accepted payers validation (if insurance is accepted)
  if (
    data.pricingBilling.acceptInsurance &&
    (!data.pricingBilling.acceptedPayers ||
      data.pricingBilling.acceptedPayers.length === 0)
  ) {
    errors.acceptedPayers =
      'At least one payer must be selected when accepting insurance'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const validateStep4SchedulingRules = (
  data: ServicePackageFormData
): StepValidationResult => {
  const errors: ValidationErrors = {}

  // Session length validation
  if (
    !data.schedulingRules.sessionLengthMin ||
    data.schedulingRules.sessionLengthMin < 15 ||
    data.schedulingRules.sessionLengthMin > 120
  ) {
    errors.sessionLengthMin =
      'Session length must be between 15 and 120 minutes'
  }

  // In-person settings validation (if modality includes in-person)
  if (
    data.basics.modality.includes('in_person') ||
    data.basics.modality.includes('hybrid')
  ) {
    if (data.schedulingRules.inPersonSettings.serviceArea === 'radius_miles') {
      if (
        !data.schedulingRules.inPersonSettings.radiusMiles ||
        data.schedulingRules.inPersonSettings.radiusMiles < 1
      ) {
        errors['inPersonSettings.radiusMiles'] =
          'Service radius must be at least 1 mile'
      }
    } else if (
      data.schedulingRules.inPersonSettings.serviceArea === 'fixed_zip_list'
    ) {
      if (
        !data.schedulingRules.inPersonSettings.zipCodes ||
        data.schedulingRules.inPersonSettings.zipCodes.length === 0
      ) {
        errors['inPersonSettings.zipCodes'] =
          'At least one ZIP code is required'
      } else {
        // Validate each ZIP code format (5 digits)
        data.schedulingRules.inPersonSettings.zipCodes.forEach(
          (zipCode, index) => {
            if (!/^\d{5}$/.test(zipCode)) {
              errors[`inPersonSettings.zipCodes[${index}]`] =
                'ZIP code must be 5 digits'
            }
          }
        )
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const validateStep5Documentation = (
  data: ServicePackageFormData
): StepValidationResult => {
  const errors: ValidationErrors = {}

  // Reports required validation
  if (
    !data.documentation.reportsRequired ||
    data.documentation.reportsRequired.length === 0
  ) {
    errors.reportsRequired = 'At least one report type is required'
  }

  // Export formats validation
  // if (
  //   !data.documentation.exportFormats ||
  //   data.documentation.exportFormats.length === 0
  // ) {
  //   errors.exportFormats = 'At least one export format is required'
  // }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const validateStep6VisibilityMatching = (
  data: ServicePackageFormData
): StepValidationResult => {
  const errors: ValidationErrors = {}

  // Specialties validation
  // if (
  //   !data.visibilityMatching.specialties ||
  //   data.visibilityMatching.specialties.length === 0
  // ) {
  //   errors.specialties = 'At least one specialty is required'
  // }

  // Body regions validation
  if (
    !data.visibilityMatching.bodyRegions ||
    data.visibilityMatching.bodyRegions.length === 0
  ) {
    errors.bodyRegions = 'At least one body region is required'
  }

  // Keywords validation (optional but if provided, must be valid)
  if (data.visibilityMatching.keywords) {
    data.visibilityMatching.keywords.forEach((keyword, index) => {
      if (!keyword.trim()) {
        errors[`keywords.${index}`] = 'Keyword cannot be empty'
      }
    })
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

export const validateAllSteps = (
  data: ServicePackageFormData
): StepValidationResult => {
  const allErrors: ValidationErrors = {}
  let isValid = true

  // Validate each step
  const step1Validation = validateStep1Basics(data)
  const step2Validation = validateStep2ClinicalCoding(data)
  const step3Validation = validateStep3PricingBilling(data)
  const step4Validation = validateStep4SchedulingRules(data)
  const step5Validation = validateStep5Documentation(data)
  const step6Validation = validateStep6VisibilityMatching(data)

  // Collect all errors
  if (!step1Validation.isValid) {
    allErrors.basics = step1Validation.errors
    isValid = false
  }

  if (!step2Validation.isValid) {
    allErrors.clinicalCoding = step2Validation.errors
    isValid = false
  }

  if (!step3Validation.isValid) {
    allErrors.pricingBilling = step3Validation.errors
    isValid = false
  }

  if (!step4Validation.isValid) {
    allErrors.schedulingRules = step4Validation.errors
    isValid = false
  }

  if (!step5Validation.isValid) {
    allErrors.documentation = step5Validation.errors
    isValid = false
  }

  if (!step6Validation.isValid) {
    allErrors.visibilityMatching = step6Validation.errors
    isValid = false
  }

  return {
    isValid,
    errors: allErrors,
  }
}

// Helper function to get error message for a specific field
export const getFieldError = (
  errors: ValidationErrors,
  fieldPath: string
): string | undefined => {
  const pathParts = fieldPath.split('.')
  let current = errors

  for (const part of pathParts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part] as ValidationErrors
    } else {
      return undefined
    }
  }

  return typeof current === 'string' ? current : undefined
}
